// src/components/OrderEntry/panels/PartPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import GridBadge from '../components/GridBadge.jsx';

/**
 * Area 3 — Part + Shipping + Flags
 * - Part selection + qty/uom/price
 * - Shipping (shipvia, carrier, freight payment)
 * - Flags (insurance, split, spec order, house, international)
 */

// ============================================================================
// BLOCK OEPART.1 — Part option normalization + selection helpers
// ============================================================================
function normalizePartKey(s) {
  return String(s ?? '')
    .replace(/[\s-]/g, '')
    .toLowerCase();
}

function resolvePartOption(options, rawInput) {
  const key = normalizePartKey(rawInput);
  if (!key) {
    return null;
  }

  const exact = options.find((o) => normalizePartKey(o?.value || o?.label) === key);
  if (exact) {
    return exact;
  }

  const prefix = options.find((o) => normalizePartKey(o?.value || o?.label).startsWith(key));
  return prefix || null;
}

export default function PartPanel({
  company,
  disabled,
  directShipPatientActive,

  // layout
  inputHeight = 56,
  partFieldsWidth = 200,
  colWidth = 420,

  // part
  partOptions,
  partLoading,
  partNo,
  setPartNo,
  partInput,
  setPartInput,
  handlePartLookup,
  onAddPart, // queue / picking sheet

  // ============================================================================
  // BLOCK OEPART.PROPS.0 — Price edit callback prop
  // Purpose:
  //   Allow controller to mark price as "user-touched" so auto-pricing won't overwrite.
  // Inputs:
  //   onPriceUserEdit(boolean)
  // Outputs:
  //   None (callback only)
  // Notes:
  //   Optional; safe no-op if not provided.
  // ============================================================================
  // qty/uom/price
  qty,
  setQty,
  uom,
  setUom,
  price,
  setPrice,
  formatMoneyInput,
  parseQtyInt,
  orderTotalText,
  priceWarningText,
  onPriceUserEdit,

  // TZI Sugar quote prompt (shown only after Part No commit)
  productQuote,
  productQuoteDialogOpen = false,
  productQuoteDefaultUsePrice = false,
  onAcceptProductQuote,
  onDeclineProductQuote,

  // shipping
  shipVia,
  setShipVia,
  shipViaList, // [{ code, description }]
  carrier,
  setCarrier,
  frtPay,
  setFrtPay,
  shipEtaMap,

  // insurance
  zeroInsurance,
  setZeroInsurance,

  // terms
  termsValue,
  setTermsValue,
  termsOptions,
  onZeroInsuranceManual, // optional callback to flip the manual override ref

  // account + contract
  frtAccount,
  setFrtAccount,
  onFrtAccountUserEdit,
  onFrtAccountBlur,
  contractCode,
  setContractCode,

  contractNo,

  setContractNo,

  // flags
  splitCommission,
  setSplitCommission,

  specOrderBo,
  setSpecOrderBo,
  specOrderMo,
  setSpecOrderMo,
  specOrderDs,
  setSpecOrderDs,
  specOrderBl,
  setSpecOrderBl,

  houseAccount,
  setHouseAccount,
  omitCommentCodes,
  setOmitCommentCodes,
  customCommentsText,
  setCustomCommentsText,
  internationalSale,
  setInternationalSale,
  partRef,
  qtyRef,
  phoneRef,
  addPartButtonRef,
  sugarButtonRef,
  confirmTo,
  phone,
}) {
  const [partOpen, setPartOpen] = useState(false);
  const [freeFreightShipViaGuardOpen, setFreeFreightShipViaGuardOpen] = useState(false);

  const partOptionList = Array.isArray(partOptions) ? partOptions : [];
  const shipViaOptions = useMemo(() => {
    return Array.isArray(shipViaList) ? shipViaList : [];
  }, [shipViaList]);
  const termsOptionList = Array.isArray(termsOptions) ? termsOptions : [];

  const confirmFilled = String(confirmTo ?? '').trim().length > 0;
  const phoneFilled = String(phone ?? '').trim().length > 0;
  const isTziCompany =
    String(company || '')
      .trim()
      .toLowerCase() === 'tzi';

  // ---------------------------------------------------------------------------
  // BLOCK OEPART.SHIPVIA.USERTRACK.0 — Track last user-selected ShipVia + manual Free
  // Purpose:
  //  - Prevent autopilot from snapping ShipVia back to FDX-GND* after user picks 2D, etc.
  //  - Allow "manual FreeFrt" on non-ground ShipVia (user intent) without forcing ground.
  // ---------------------------------------------------------------------------
  const lastUserShipViaRef = useRef('');
  const lastUserShipViaAtRef = useRef(0);
  const manualFreeRef = useRef(false);
  const manualFreeAtRef = useRef(0);

  function isGroundLikeShipVia(code) {
    const s = String(code || '')
      .trim()
      .toUpperCase();
    // treat any "FDX-GND" variant as ground-like
    return s === 'FDX-GND' || s.startsWith('FDX-GND-');
  }

  // ---------------------------------------------------------------------------
  // BLOCK OEPART.SHIPVIA.UPS_DEFAULT.0 — Default UPS ShipVia to UPS-GND
  // Rule:
  //  - When carrier becomes UPS (user click OR auto-selected from freight acct),
  //    default ShipVia to UPS-GND.
  // Guardrails:
  //  - Do NOT override a user-picked UPS service (e.g., UPS-2D).
  //  - Only apply when ShipVia is blank, non-UPS, or currently the bad default UPS-1D.
  // ---------------------------------------------------------------------------
  const prevCarrierRef = useRef(String(carrier || '').trim());

  useEffect(() => {
    const prev = String(prevCarrierRef.current || '')
      .trim()
      .toUpperCase();
    const curr = String(carrier || '')
      .trim()
      .toUpperCase();
    prevCarrierRef.current = String(carrier || '').trim();

    // Only react on transition into UPS
    if (curr !== 'UPS' || prev === 'UPS') return;

    const currentShipVia = String(shipVia || '')
      .trim()
      .toUpperCase();

    const shouldDefault =
      !currentShipVia || // blank
      !currentShipVia.startsWith('UPS-') || // coming from FedEx/USPS/Other
      currentShipVia === 'UPS-1D'; // currently wrong default

    if (shouldDefault) {
      // System default action (NOT a user selection) — don't touch lastUserShipViaRef.
      setShipVia('UPS-GND');
    }
  }, [carrier, shipVia, setShipVia]);

  useEffect(() => {
    const last = String(lastUserShipViaRef.current || '').trim();
    if (!last) {
      return;
    }

    const shipViaCodes = shipViaOptions
      .map((item) => String(item?.code || '').trim())
      .filter(Boolean);

    if (!shipViaCodes.includes(last)) {
      return;
    }

    const now = Date.now();
    const ageMs = now - (lastUserShipViaAtRef.current || 0);

    // only react very soon after a user action (avoid fighting normal controller updates)
    if (ageMs > 600) {
      return;
    }

    const current = String(shipVia || '').trim();

    // If something snaps ShipVia to ground-like, but user just picked a non-ground option,
    // restore the user choice.
    if (isGroundLikeShipVia(current) && !isGroundLikeShipVia(last) && current !== last) {
      // Only restore aggressively when:
      //  - user JUST chose ShipVia, OR
      //  - user JUST manually clicked FreeFrt (we want to keep their ShipVia)
      const manualFreeAge = now - (manualFreeAtRef.current || 0);
      const manualFreeRecently = manualFreeRef.current && manualFreeAge <= 800;

      if (manualFreeRecently || ageMs <= 600) {
        setShipVia(last);
      }
    }
  }, [shipVia, setShipVia, shipViaOptions]);

  // ---------------------------------------------------------------------------
  // BLOCK OEPART.TOTAL.IMMEDIATE.0 — Compute an immediate order total from Qty+Price
  // Purpose:
  //  - Make $0-order detection work immediately (before controller orderTotalText updates).
  //  - Also improves UI by showing a real-time total in the helper line.
  // Notes:
  //  - Returns null if qty/price is not yet valid.
  // ---------------------------------------------------------------------------
  function computeImmediateOrderTotalNumber(qtyRaw, priceRaw) {
    const q = parseQtyInt?.(qtyRaw);
    if (!q || q <= 0) {
      return null;
    }

    const s = String(priceRaw ?? '').trim();
    if (!s) {
      return null;
    }
    const p = Number(s.replace(/[$,]/g, ''));
    if (!Number.isFinite(p)) {
      return null;
    }

    const total = q * p;
    return Number.isFinite(total) ? total : null;
  }

  function formatUsd(n) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
    } catch {
      return `$${Number(n || 0).toFixed(2)}`;
    }
  }

  // ---------------------------------------------------------------------------
  // BLOCK OEPART.TOTAL.IMMEDIATE.DERIVED.0 — Render-safe helper text (no nested JSX)
  // ---------------------------------------------------------------------------
  const immediateTotalForDisplay = computeImmediateOrderTotalNumber(qty, price);
  const orderTotalHelperText =
    immediateTotalForDisplay !== null
      ? `Order Total: ${formatUsd(immediateTotalForDisplay)}`
      : orderTotalText;

  // BLOCK OEPART.3A — Add Part gate
  // - Only enabled if a handler is provided (queue feature wired)
  // - Requires a resolved partNo, qty > 0, and a non-blank price
  const canAddPart =
    typeof onAddPart === 'function' &&
    !disabled &&
    String(partNo ?? '').trim().length > 0 &&
    (parseQtyInt?.(qty) ?? 0) > 0 &&
    String(price ?? '').trim().length > 0 &&
    confirmFilled &&
    phoneFilled;

  // ==========================================================================
  // BLOCK OEPART.2 — Freight payment change handler (hook point for FC->PPA guard)
  // ==========================================================================
  const handleFrtPayChange = (e) => {
    const next = String(e.target.value || '').trim();

    // User manually clicked FreeFrt: allow Free on whatever ShipVia they selected.
    if (next.toUpperCase() === 'FREE') {
      manualFreeRef.current = true;
      manualFreeAtRef.current = Date.now();
    } else {
      manualFreeRef.current = false;
      manualFreeAtRef.current = 0;
    }

    setFrtPay(next);
  };

  const handleCarrierChange = (e) => {
    const next = e.target.value;
    setCarrier(next);
  };

  // ==========================================================================
  // BLOCK OEPART.ZERO_PRICE_DIALOG.0 — $0 price -> ask to apply Free freight
  // ==========================================================================

  // Parse "$1,234.56" or "0" or "" into number
  function parseMoneyLoose(v) {
    const s = String(v ?? '').trim();
    if (!s) {
      return null;
    }
    const n = Number(s.replace(/[$,]/g, ''));
    return Number.isFinite(n) ? n : null;
  }

  // Parse "Order Total: $0.00" (best-effort) -> number OR null if no number present
  function parseOrderTotalNumber(text) {
    const s = String(text || '').trim();
    if (!s) {
      return null;
    }

    const m = s.match(/-?\d[\d,]*\.?\d*/);
    if (!m) {
      return null; // unknown total
    }

    const n = Number(String(m[0]).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  }

  // ==========================================================================
  // BLOCK OEPART.SHIPVIA.FREE_GUARD.0 — Leaving FDX-GND-FC demotes Free freight
  // Rule:
  //  - If user changes ShipVia away from FDX-GND-FC while frtPay is Free,
  //    then demote to:
  //      • FC if Freight Acct has a value
  //      • otherwise PPA
  // Exception:
  //  - If entire order total is $0.00, keep Free (0$ order behavior).
  // ==========================================================================
  const handleShipViaChange = (e) => {
    const next = String(e.target.value || '').trim();

    // Track what the user chose so we can restore if something else snaps back to ground.
    lastUserShipViaRef.current = next;
    lastUserShipViaAtRef.current = Date.now();

    setShipVia(next);

    const isCurrentlyFree =
      String(frtPay || '')
        .trim()
        .toUpperCase() === 'FREE';

    if (!isCurrentlyFree) {
      return;
    }

    // For TMS ShipTo P direct-ship mode, FreeFrt remains locked while users can change ShipVia.
    if (directShipPatientActive) {
      return;
    }

    // Treat both "FDX-GND" and "FDX-GND-FC" as the Free-eligible ground option(s)
    const nextUpper = next.toUpperCase();
    const isFreeEligibleShipVia = nextUpper === 'FDX-GND' || nextUpper.startsWith('FDX-GND-');
    const isExactFreeGroundShipVia = nextUpper === 'FDX-GND';

    // Preserve 0$ order behavior ONLY when we can confidently read a numeric total.
    // First try immediate (Qty*Price). If not available yet, fall back to orderTotalText.
    const immediateTotal = computeImmediateOrderTotalNumber(qty, price);
    const fallbackTotal = parseOrderTotalNumber(orderTotalText);
    const orderTotal = immediateTotal !== null ? immediateTotal : fallbackTotal;

    const isZeroDollarOrder = orderTotal !== null && orderTotal <= 0;
    if (isZeroDollarOrder) {
      return;
    }

    // Leaving the free-eligible ground shipvia demotes Free -> FC/PPA
    if (!isFreeEligibleShipVia) {
      if (isTziCompany && !isExactFreeGroundShipVia) {
        setFreeFreightShipViaGuardOpen(true);
        return;
      }

      const hasAcct = String(frtAccount || '').trim().length > 0;
      setFrtPay(hasAcct ? 'FC' : 'PPA');
    }
  };

  const handleKeepFreeFreight = () => {
    manualFreeRef.current = true;
    manualFreeAtRef.current = Date.now();
    setFrtPay('Free');
    setFreeFreightShipViaGuardOpen(false);
  };

  function normalizeShipViaForFreightPay(selectedShipVia, nextFrtPay) {
    const raw = String(selectedShipVia || '').trim();
    if (!raw) {
      return raw;
    }

    const isFcTarget =
      String(nextFrtPay || '')
        .trim()
        .toUpperCase() === 'FC';
    const base = raw
      .replace(/\s*\(FC\)\s*$/i, '')
      .replace(/-FC$/i, '')
      .trim();
    const baseUpper = base.toUpperCase();
    const carrierPrefix = baseUpper.split('-')[0] || '';
    const sourceOptions = Array.isArray(shipViaOptions) ? shipViaOptions : [];
    const optionCodes = sourceOptions
      .map((item) => String(item?.code || '').trim())
      .filter(Boolean);

    const findOption = (predicate) => optionCodes.find((code) => predicate(code.toUpperCase()));

    if (isFcTarget) {
      const exactHyphenFc = findOption((upper) => upper === `${baseUpper}-FC`);
      if (exactHyphenFc) {
        return exactHyphenFc;
      }

      const exactParenFc = findOption((upper) => upper === `${baseUpper} (FC)`);
      if (exactParenFc) {
        return exactParenFc;
      }

      // Closest: same carrier + same service token + FC marker.
      const sameServiceFc = findOption((upper) => {
        const nonFcUpper = upper
          .replace(/\s*\(FC\)\s*$/i, '')
          .replace(/-FC$/i, '')
          .trim();
        return (
          (upper.endsWith('-FC') || /\(FC\)$/.test(upper)) &&
          nonFcUpper === baseUpper &&
          (!carrierPrefix || upper.startsWith(`${carrierPrefix}-`))
        );
      });
      if (sameServiceFc) {
        return sameServiceFc;
      }

      // Never fabricate a code that is not in the dropdown; keep current selection.
      return raw;
    }

    // PPA/Free should show the non-collect service code.
    const exactBase = findOption((upper) => upper === baseUpper);
    if (exactBase) {
      return exactBase;
    }

    return base;
  }

  const handleChargeFreight = () => {
    manualFreeRef.current = false;
    manualFreeAtRef.current = 0;
    const hasAcct = String(frtAccount || '').trim().length > 0;
    const nextFrtPay = hasAcct ? 'FC' : 'PPA';

    // Keep the user's currently selected service level when switching pay mode.
    const nextShipVia = normalizeShipViaForFreightPay(
      lastUserShipViaRef.current || shipVia,
      nextFrtPay
    );
    if (nextShipVia) {
      setShipVia(nextShipVia);
      lastUserShipViaRef.current = nextShipVia;
      lastUserShipViaAtRef.current = Date.now();
    }

    setFrtPay(nextFrtPay);
    setFreeFreightShipViaGuardOpen(false);
  };

  const [zeroPriceDlgOpen, setZeroPriceDlgOpen] = useState(false);
  const [zeroPricePrompted, setZeroPricePrompted] = useState(false);

  const shouldIgnoreZeroPriceDialog = () => {
    return (
      Boolean(directShipPatientActive) ||
      String(frtPay || '')
        .trim()
        .toUpperCase() === 'FREE'
    );
  };

  const maybePromptZeroPrice = () => {
    if (shouldIgnoreZeroPriceDialog()) {
      return false;
    }

    // Use formatted price if possible
    const formatted = formatMoneyInput(price);
    const n = parseMoneyLoose(formatted);

    // Only prompt on explicit $0.00 (not blank / not invalid)
    if (n === 0 && !zeroPricePrompted) {
      setZeroPriceDlgOpen(true);
      setZeroPricePrompted(true);
      return true;
    }
    return false;
  };

  const handleZeroPriceYes = () => {
    // System action (dialog) — not a manual FreeFrt intent
    manualFreeRef.current = false;
    manualFreeAtRef.current = 0;

    // Apply Free freight + force ground shipvia
    setShipVia('FDX-GND-FC');
    setFrtPay('Free');
    setZeroPriceDlgOpen(false);
  };

  const handleZeroPriceNo = () => {
    setZeroPriceDlgOpen(false);
  };

  // ==========================================================================
  // BLOCK OEPART.QUOTE.0 — TZI Sugar quote dialog formatting
  // ==========================================================================
  const quoteDisplayPart = String(productQuote?.name || productQuote?.mftPartNum || '').trim();
  const quotePriceNumber = Number(productQuote?.discountPrice);
  const quoteHasPrice =
    productQuote?.discountPrice != null &&
    String(productQuote.discountPrice).trim() !== '' &&
    Number.isFinite(quotePriceNumber);
  const quoteQuantityNumber = Number(productQuote?.quantity);
  const quoteHasQuantity = Number.isFinite(quoteQuantityNumber) && quoteQuantityNumber !== 0;
  const quoteDateText = (() => {
    const raw = String(productQuote?.dateEntered || '').trim();
    if (!raw) return '';

    // Preserve the stored Sugar calendar date without timezone shifting it.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (dateOnly) {
      return `${Number(dateOnly[2])}/${Number(dateOnly[3])}/${dateOnly[1]}`;
    }

    const parsed = new Date(raw);
    if (!Number.isFinite(parsed.getTime())) return raw;
    return parsed.toLocaleDateString();
  })();

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        px: 2,
        py: 1.5,
        position: 'relative',
        display: 'inline-block',
        width: 'auto',
        maxWidth: 'none',
        minWidth: 0,
      }}
    >
      <Dialog
        open={Boolean(productQuoteDialogOpen && productQuote)}
        onClose={() => onDeclineProductQuote?.()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {productQuote?.outOfDate ? 'Out of Date Quote Exists.' : 'Quote exists for this customer'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            {productQuote?.outOfDate ? (
              <Typography sx={{ fontWeight: 700 }}>Quote exists for this customer</Typography>
            ) : null}
            <Typography>
              <strong>Date:</strong> {quoteDateText || 'Unknown'}
            </Typography>
            <Typography>
              <strong>Part:</strong> {quoteDisplayPart || String(partNo || '').trim()}
            </Typography>
            <Typography>
              <strong>Price:</strong>{' '}
              {quoteHasPrice ? formatUsd(quotePriceNumber) : 'Not available'}
            </Typography>
            {quoteHasQuantity ? (
              <Typography>
                <strong>Quantity:</strong> {quoteQuantityNumber}
              </Typography>
            ) : null}
            <Typography sx={{ pt: 1, fontWeight: 800 }}>Use this price?</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => onDeclineProductQuote?.()}
            variant={productQuoteDefaultUsePrice ? 'outlined' : 'contained'}
            autoFocus={!productQuoteDefaultUsePrice}
          >
            No
          </Button>
          <Button
            onClick={() => onAcceptProductQuote?.()}
            variant={productQuoteDefaultUsePrice ? 'contained' : 'outlined'}
            autoFocus={productQuoteDefaultUsePrice}
            disabled={!quoteHasPrice}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <GridBadge n="3" />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${partFieldsWidth}px ${colWidth}px ${colWidth}px`,
          columnGap: 2,
          alignItems: 'start',
          minWidth: 0,
        }}
      >
        {/* ================================================================== */}
        {/* BLOCK OEPART.10 — Column 1: Part / Qty / UOM / Price */}
        {/* ================================================================== */}
        <Box
          sx={{
            pr: 2,
            borderRight: '1px solid',
            borderColor: 'divider',
            boxSizing: 'border-box',
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'grid', rowGap: 1 }}>
            <Autocomplete
              disabled={disabled}
              disableClearable
              options={partOptionList}
              loading={partLoading}
              autoComplete
              autoHighlight
              autoSelect
              open={partOpen}
              onOpen={() => setPartOpen(true)}
              onClose={() => setPartOpen(false)}
              value={partOptionList.find((o) => o.value === partNo) || null}
              inputValue={partInput}
              getOptionLabel={(option) => option?.label || ''}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              filterOptions={(options, state) => {
                const needle = normalizePartKey(state.inputValue);
                if (!needle) {
                  return options;
                }

                return options.filter((o) =>
                  normalizePartKey(o?.label || o?.value).includes(needle)
                );
              }}
              onInputChange={(e, newInput) => {
                setPartInput(newInput);
                if (newInput) {
                  setPartOpen(true);
                }

                const hit = resolvePartOption(partOptionList, newInput);
                if (
                  hit &&
                  normalizePartKey(hit?.value || hit?.label) === normalizePartKey(newInput)
                ) {
                  setPartNo(hit.value);
                }
              }}
              onChange={(event, newValue) => {
                const chosen = newValue?.value || '';
                setPartNo(chosen);
                setPartInput(newValue?.label || chosen);
                setPartOpen(false);

                if (chosen && typeof handlePartLookup === 'function') {
                  handlePartLookup(chosen);
                }
              }}
              renderInput={(params) => {
                const patched = {
                  ...params,
                  inputProps: { ...params.inputProps, tabIndex: 5 },
                };

                return (
                  <TextField
                    {...patched}
                    label="Part No"
                    placeholder="Select part"
                    inputRef={partRef}
                    sx={{
                      ...patched.sx,
                      width: '100%',
                      '& .MuiInputBase-root': { height: inputHeight },
                      '& .MuiInputBase-input': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                    onBlur={() => {
                      setPartOpen(false);

                      const hit = resolvePartOption(partOptionList, partInput);
                      if (hit) {
                        if (hit.value !== partNo) {
                          setPartNo(hit.value);
                        }
                        setPartInput(hit.label || hit.value);

                        if (typeof handlePartLookup === 'function') {
                          handlePartLookup(hit.value);
                        }
                        return;
                      }

                      if (partNo && typeof handlePartLookup === 'function') {
                        handlePartLookup(partNo);
                      }
                    }}
                  />
                );
              }}
            />

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'start' }}>
              <TextField
                label="Qty"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                disabled={disabled}
                inputRef={qtyRef}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', tabIndex: 6 }}
                onBlur={() => {
                  const q = parseQtyInt(qty);
                  setQty(q == null ? '' : String(q));
                }}
                sx={{
                  width: 70,
                  '& .MuiInputBase-root': { height: inputHeight },
                  '& .MuiInputBase-input': { textAlign: 'right' },
                }}
              />

              <TextField
                label="UOM"
                value={uom}
                onChange={(e) => setUom(e.target.value)}
                disabled={disabled}
                sx={{
                  width: 120,
                  '& .MuiInputBase-root': { height: inputHeight },
                  '& .MuiInputBase-input': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            </Box>

            <TextField
              label="Price"
              value={price}
              onChange={(e) => {
                if (typeof onPriceUserEdit === 'function') {
                  onPriceUserEdit(true);
                }
                setPrice(e.target.value);
                setZeroPricePrompted(false);
              }}
              disabled={disabled}
              onBlur={() => {
                const formatted = formatMoneyInput(price);
                setPrice(formatted);

                // If $0.00 entered, ask whether to apply free freight (unless ignoring rule)
                const n = parseMoneyLoose(formatted);
                if (n === 0 && !shouldIgnoreZeroPriceDialog() && !zeroPricePrompted) {
                  setZeroPriceDlgOpen(true);
                  setZeroPricePrompted(true);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && !e.shiftKey && !canAddPart) {
                  e.preventDefault();
                  if (!phoneFilled) {
                    phoneRef?.current?.focus?.();
                    return;
                  }

                  sugarButtonRef?.current?.focus?.();
                }
              }}
              inputProps={{ tabIndex: 7 }}
              helperText={
                <Box sx={{ lineHeight: 1.2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {orderTotalHelperText}
                  </Typography>

                  {String(priceWarningText ?? '').trim() && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.25 }}>
                      {priceWarningText}
                    </Typography>
                  )}
                </Box>
              }
              sx={{
                width: 120,
                '& .MuiInputBase-root': { height: inputHeight },
                '& .MuiInputBase-input': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'right',
                },
              }}
            />
            {typeof onAddPart === 'function' && (
              <Button
                variant="contained"
                onClick={() => {
                  // Fire $0 dialog BEFORE add part. If dialog opens, block add.
                  const opened = maybePromptZeroPrice();
                  if (opened) {
                    return; // user must click Add Part again after answering
                  }
                  onAddPart();
                }}
                disabled={!canAddPart}
                ref={addPartButtonRef}
                tabIndex={8}
                sx={{ mt: 1, alignSelf: 'flex-start' }}
              >
                Add Part
              </Button>
            )}
          </Box>
        </Box>

        {/* ================================================================== */}
        {/* BLOCK OEPART.20 — Column 2: Shipping (ShipVia + radios + terms) */}
        {/* ================================================================== */}
        <Box sx={{ minWidth: 0 }}>
          <Stack spacing={1}>
            {/* Row 1: Ship Via */}
            <FormControl size="small" sx={{ minWidth: 250, flexShrink: 0 }} disabled={disabled}>
              <InputLabel id="shipvia-label">Ship Via</InputLabel>
              <Select
                labelId="shipvia-label"
                label="Ship Via"
                value={shipVia}
                onChange={handleShipViaChange}
                tabIndex={-1}
                inputProps={{ tabIndex: -1 }}
                renderValue={(selected) => {
                  const etaLabel = shipEtaMap?.[selected]?.label || '';
                  return etaLabel ? `${selected} — ${etaLabel}` : selected;
                }}
              >
                {shipViaOptions.map((item) => {
                  const code = String(item?.code || '').trim();
                  const etaLabel = shipEtaMap?.[code]?.label || '';
                  return (
                    <MenuItem key={code} value={code}>
                      {etaLabel ? `${code} — ${etaLabel}` : code}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Row 2: Carrier + Freight Payment radios */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <FormControl disabled={disabled} sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.25 }}>
                  Carrier
                </Typography>
                <RadioGroup row value={carrier} onChange={handleCarrierChange}>
                  <FormControlLabel
                    value="FDX"
                    control={<Radio inputProps={{ tabIndex: -1 }} />}
                    label="FEDEX"
                  />
                  <FormControlLabel
                    value="UPS"
                    control={<Radio inputProps={{ tabIndex: -1 }} />}
                    label="UPS"
                  />
                  <FormControlLabel
                    value="USPS"
                    control={<Radio inputProps={{ tabIndex: -1 }} />}
                    label="USPS"
                  />
                  <FormControlLabel
                    value="OTHER"
                    control={<Radio inputProps={{ tabIndex: -1 }} />}
                    label="OTHER"
                  />
                </RadioGroup>
              </FormControl>

              <FormControl disabled={disabled || directShipPatientActive} sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.25 }}>
                  Freight Payment
                </Typography>
                <RadioGroup row value={frtPay} onChange={handleFrtPayChange}>
                  <FormControlLabel
                    value="Free"
                    control={<Radio inputProps={{ tabIndex: -1 }} />}
                    label="FreeFrt"
                  />
                  <FormControlLabel
                    value="PPA"
                    control={<Radio inputProps={{ tabIndex: -1 }} />}
                    label="PPA"
                  />
                  <FormControlLabel
                    value="FC"
                    control={<Radio inputProps={{ tabIndex: -1 }} />}
                    label="Collect"
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {/* Row 3: Terms + $0 Insurance */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(220px, 1fr) max-content',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <FormControl size="small" sx={{ minWidth: 0 }}>
                <InputLabel id="terms-label">Terms</InputLabel>
                <Select
                  labelId="terms-label"
                  label="Terms"
                  value={termsValue}
                  onChange={(e) => setTermsValue(String(e.target.value))}
                  disabled={disabled}
                  tabIndex={-1}
                  inputProps={{ tabIndex: -1 }}
                  renderValue={(selected) => {
                    const hit = termsOptionList.find((o) => String(o.value) === String(selected));
                    return hit?.label || '';
                  }}
                >
                  {termsOptionList.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={zeroInsurance}
                    onChange={(e) => {
                      if (typeof onZeroInsuranceManual === 'function') {
                        onZeroInsuranceManual(true);
                      }
                      setZeroInsurance(e.target.checked);
                    }}
                    disabled={disabled}
                    inputProps={{ tabIndex: -1 }}
                  />
                }
                label="$0 Insurance"
                sx={{ m: 0 }}
              />
            </Box>
          </Stack>
        </Box>

        {/* ============================================================================
           BLOCK PP.HOUSEFLAGS.UI.0 — Flags + Freight/Contract (RIGHT column)
           - Right column: flags on top, freight acct + contract below (Excel-like)
           ============================================================================ */}
        <Box sx={{ minWidth: 0 }}>
          {/*
            BLOCK PP.HOUSEFLAGS.DERIVED.0 — Human-readable flag summary text
            Purpose:
              - Populate the "Flag:" display line (Excel parity / quick glance).
              - Purely derived from existing boolean props (no new state).
          */}
          {(() => {
            const parts = [];

            // Special order (exclusive in UI, but be defensive)
            if (specOrderBo) {
              parts.push('BO');
            }
            if (specOrderMo) {
              parts.push('MO');
            }
            if (specOrderDs) {
              parts.push('DS');
            }
            if (specOrderBl) {
              parts.push('BL');
            }

            if (splitCommission) {
              parts.push('Split');
            }
            if (houseAccount) {
              parts.push('House');
            }
            if (internationalSale) {
              parts.push('Intl');
            }
            if (zeroInsurance) {
              parts.push('$0 Ins');
            }

            const text = parts.join(' · ');
            return (
              <Stack spacing={1}>
                {/* Flag: display row */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, flexShrink: 0 }}>
                    Flag:
                  </Typography>
                  <Typography
                    variant="caption"
                    color={text ? 'text.primary' : 'text.secondary'}
                    sx={{
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={text || ''}
                  >
                    {text || '—'}
                  </Typography>
                </Box>

                {/* Flags (2 columns) */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(splitCommission)}
                        onChange={(e) => setSplitCommission(Boolean(e.target.checked))}
                        disabled={disabled}
                        inputProps={{ tabIndex: -1 }}
                      />
                    }
                    label="Split Commission"
                  />

                  {/* ================================================================
                      PP.SPEC_ORDER.CHECKBOX.0 — Special order type (exclusive)
                     ================================================================ */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(specOrderBo)}
                        onChange={(e) => {
                          const next = Boolean(e.target.checked);
                          setSpecOrderBo(next);
                          if (next) {
                            setSpecOrderMo(false);
                            setSpecOrderDs(false);
                            setSpecOrderBl(false);
                          }
                        }}
                        disabled={disabled}
                        inputProps={{ tabIndex: -1 }}
                      />
                    }
                    label="Special Order BO"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(specOrderMo)}
                        onChange={(e) => {
                          const next = Boolean(e.target.checked);
                          setSpecOrderMo(next);
                          if (next) {
                            setSpecOrderBo(false);
                            setSpecOrderDs(false);
                            setSpecOrderBl(false);
                          }
                        }}
                        disabled={disabled}
                        inputProps={{ tabIndex: -1 }}
                      />
                    }
                    label="Special Order MO"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(specOrderDs)}
                        onChange={(e) => {
                          const next = Boolean(e.target.checked);
                          setSpecOrderDs(next);
                          if (next) {
                            setSpecOrderBo(false);
                            setSpecOrderMo(false);
                            setSpecOrderBl(false);
                          }
                        }}
                        disabled={disabled}
                        inputProps={{ tabIndex: -1 }}
                      />
                    }
                    label="Direct Ship (DS)"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(specOrderBl)}
                        onChange={(e) => {
                          const next = Boolean(e.target.checked);
                          setSpecOrderBl(next);
                          if (next) {
                            setSpecOrderBo(false);
                            setSpecOrderMo(false);
                            setSpecOrderDs(false);
                          }
                        }}
                        disabled={disabled}
                        inputProps={{ tabIndex: -1 }}
                      />
                    }
                    label="Bill Only (BL)"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(houseAccount)}
                        onChange={(e) => setHouseAccount(Boolean(e.target.checked))}
                        disabled={disabled}
                        inputProps={{ tabIndex: -1 }}
                      />
                    }
                    label="House Account"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(internationalSale)}
                        onChange={(e) => setInternationalSale(Boolean(e.target.checked))}
                        disabled={disabled}
                        inputProps={{ tabIndex: -1 }}
                      />
                    }
                    label="International Sale"
                  />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(omitCommentCodes)}
                          onChange={(e) => setOmitCommentCodes(Boolean(e.target.checked))}
                          disabled={disabled}
                          inputProps={{ tabIndex: -1 }}
                        />
                      }
                      label="Custom Comments"
                    />

                    {omitCommentCodes ? (
                      <TextField
                        fullWidth
                        size="small"
                        label="Comment Replacement"
                        value={customCommentsText ?? ''}
                        onChange={(e) => setCustomCommentsText(e.target.value)}
                        disabled={disabled}
                        inputProps={{ tabIndex: -1 }}
                      />
                    ) : null}
                  </Box>
                </Box>

                {/* Freight Acct + Contract (RIGHT side) */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                    width: '100%',
                  }}
                >
                  <TextField
                    fullWidth
                    label="Freight Acct"
                    value={frtAccount ?? ''}
                    disabled={disabled || frtPay !== 'FC'}
                    onChange={(e) => {
                      if (typeof onFrtAccountUserEdit === 'function') {
                        onFrtAccountUserEdit(true);
                      }
                      setFrtAccount(e.target.value);
                    }}
                    onBlur={(e) => {
                      const trimmed = String(e.target.value ?? '').trim();
                      setFrtAccount(trimmed);
                      if (typeof onFrtAccountBlur === 'function') {
                        onFrtAccountBlur(trimmed);
                      }
                    }}
                    inputProps={{ tabIndex: -1 }}
                    sx={{ '& .MuiInputBase-root': { height: inputHeight } }}
                  />

                  <TextField
                    fullWidth
                    label="Contract"
                    value={contractCode ?? ''}
                    disabled={disabled}
                    onChange={(e) => setContractCode(e.target.value)}
                    onBlur={() => setContractCode(String(contractCode ?? '').trim())}
                    inputProps={{ tabIndex: -1 }}
                    sx={{ '& .MuiInputBase-root': { height: inputHeight } }}
                  />
                </Box>
              </Stack>
            );
          })()}
        </Box>
      </Box>
      {/* ====================================================================== */}
      {/* BLOCK OEPART.ZERO_PRICE_DIALOG.UI.0 — UI: Apply Free Freight?           */}
      {/* ====================================================================== */}
      <Dialog open={zeroPriceDlgOpen} onClose={handleZeroPriceNo} fullWidth maxWidth="xs">
        <DialogTitle>Apply Free Freight?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            You entered an item with <b>$0.00</b> unit price.
            <br />
            Do you want to apply <b>Free Freight</b> to this order?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleZeroPriceNo}>No</Button>
          <Button onClick={handleZeroPriceYes} variant="contained">
            Yes — Free Freight
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={freeFreightShipViaGuardOpen}
        onClose={handleKeepFreeFreight}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Freight Confirmation</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            You are selecting Free Freight other than FDX - Gnd.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleKeepFreeFreight}>FreeFreight</Button>
          <Button onClick={handleChargeFreight} variant="contained">
            Charge Frt
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
